import { defaultAxiosInstance } from './config';

import { HttpError, HttpRange, HttpResult, HttpTask } from './model';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Lazy, pipe } from 'fp-ts/function';

import * as TE from 'fp-ts/TaskEither';
import * as O from 'fp-ts/Option';
import * as EI from 'fp-ts/Either';
import * as T from 'fp-ts/Task';
import * as Retry from 'retry-ts';
import { Range, RangeCursor, RangeResult } from '@shared/modules/range';
import { Filter } from '@shared/modules/filter';
import { removeEmptyString } from '@shared/utils/string';
import { hideApiDownIndicator, showApiDownIndicator } from '@core/http/components/ApiDownIndicator';
import { retrying } from 'retry-ts/Task';

function onRequest<R, E>(request: Lazy<Promise<AxiosResponse<R>>>, raw?: true): HttpTask<R | AxiosResponse<R>, E> {
  const onError = (err: unknown): HttpError<E> => {
    const error = HttpError.fromAxiosError<E>(err as any);

    error.log();

    if (error.isDownError()) {
      showApiDownIndicator();
    }

    return error;
  };

  const transformRequest = () =>
    pipe(
      TE.tryCatch(request, err => onError(err)),
      TE.map(res => (raw ? res : res.data)),
    );

  const shouldRetry = (res: HttpResult) =>
    pipe(
      EI.swap(res),
      EI.exists(err => err.isDownError()),
    );

  return pipe(
    retrying(Retry.capDelay(2000, Retry.exponentialBackoff(500)), transformRequest, shouldRetry),
    T.chainFirstIOK(() => hideApiDownIndicator),
  );
}

function get<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E>;
function get<R = unknown, E = unknown>(
  url: string,
  config: AxiosRequestConfig,
  raw: true,
): HttpTask<AxiosResponse<R>, E>;

function get<R = unknown, E = unknown>(
  url: string,
  config: AxiosRequestConfig = {},
  raw?: true,
): HttpTask<R | AxiosResponse<R>, E> {
  return onRequest(() => defaultAxiosInstance.get(url, config), raw);
}

function getRange<R = unknown, F extends Filter = {}, E = unknown>(
  url: string,
  cursor: RangeCursor,
  filter?: F,
  config?: AxiosRequestConfig,
): HttpRange<R, F, E> {
  return pipe(
    get<RangeResult<R, F>, E>(url, {
      ...config,
      params: {
        ...config?.params,
        ...cursor,
        ...filter,
      },
    }),
    TE.map(Range.fromRangeResult),
  );
}

function post<R = unknown, E = unknown>(url: string, data?: any, config?: AxiosRequestConfig): HttpTask<R, E>;
function post<R = unknown, E = unknown>(
  url: string,
  data: any,
  config: AxiosRequestConfig,
  raw: true,
): HttpTask<AxiosResponse<R>, E>;

function post<R = unknown, E = unknown>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig,
  raw?: true,
): HttpTask<R | AxiosResponse<R>, E> {
  const filterBody = pipe(
    O.fromNullable(data),
    O.filter(data => !(data instanceof FormData)),
    O.fold(() => data, removeEmptyString),
  );

  return onRequest(() => defaultAxiosInstance.post(url, filterBody, config), raw);
}

function del<R = unknown, E = unknown>(url: string, config?: AxiosRequestConfig): HttpTask<R, E>;
function del<R = unknown, E = unknown>(
  url: string,
  config: AxiosRequestConfig,
  raw: true,
): HttpTask<R | AxiosResponse<R>, E>;

function del<R = unknown, E = unknown>(
  url: string,
  config?: AxiosRequestConfig,
  raw?: true,
): HttpTask<R | AxiosResponse<R>, E> {
  return onRequest(() => defaultAxiosInstance.delete(url, config), raw);
}

export const httpService = {
  get,
  getRange,
  post,
  delete: del,
};
